import {
  Button,
  Checkbox,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { motion, useReducedMotion } from 'motion/react';
import { Trash2Icon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { MediaFolder } from '@/entities/brand-media';
import { usePermissions } from '@/entities/user';
import { sidebarPanelSpring } from '@/shared/constants/motion.constants';

import { MoveToFolderMenu } from './MoveToFolderMenu';

/**
 * It arrives from just below its resting place, on the same spring the detail panel opens with, so
 * the two things this library reveals over its grid move alike.
 *
 * Scale rather than only a fade: the pill is a small object appearing over a busy grid of pictures,
 * and a fade alone reads as a rendering delay rather than as something turning up.
 */
const PILL_HIDDEN = { opacity: 0, y: 8, scale: 0.96 };
const PILL_SHOWN = { opacity: 1, y: 0, scale: 1 };

/**
 * What is ticked, and what can be done to it, as one bar.
 *
 * A pill rather than a row of loose controls: the selection is a mode the library is in, and a
 * shape that reads as a single object is what says the count and the actions belong to each other.
 * It follows the bar the knowledge base draws over its documents table, so a person who has picked
 * rows in one part of the app recognises this one without reading it.
 *
 * It floats over the library rather than sitting in a row of its own, and is sized to its contents
 * so it covers as little of the grid as it can. As a full-width row it took a band of the column
 * on the first tick and gave it back on the last, so ticking a box moved the box you were about to
 * tick next.
 *
 * The count is flanked by the two ways out of a selection rather than by one: the cross empties it,
 * and the checkbox extends it to everything on screen or takes those back out again. Neither is
 * ever unchecked, because the bar is not drawn at all until something is ticked.
 */
export const SelectionBar = ({
  count,
  allShownSelected,
  folders,
  onToggleAllShown,
  onClear,
  onMove,
  onDelete,
}: {
  count: number;
  /** Whether every asset and font currently on screen is ticked, which is what the box shows. */
  allShownSelected: boolean;
  folders: readonly MediaFolder[];
  onToggleAllShown: () => void;
  onClear: () => void;
  onMove: (folderId: string | null) => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation('tools');
  const shouldReduceMotion = useReducedMotion();
  const { canManageBrandAssets } = usePermissions();
  const noPermission = t('brandMediaLibrary.permissions.noPermission');

  const clearLabel = t('brandMedia.clearSelection');
  const deleteLabel = t('brandMedia.delete');

  return (
    // Its own provider: this bar is a component of its own, and a tooltip without one throws
    // rather than degrading.
    <TooltipProvider>
      <motion.div
        data-testid="brand-media-selection-bar"
        initial={shouldReduceMotion ? false : PILL_HIDDEN}
        animate={PILL_SHOWN}
        // Still faded out under reduced motion, with the travel and the scale dropped: the
        // alternative to a movement is not an instant appearance, which is the harsher of the two.
        exit={shouldReduceMotion ? { opacity: 0 } : PILL_HIDDEN}
        transition={shouldReduceMotion ? { duration: 0.1 } : sidebarPanelSpring}
        // A shadow rather than a border, because it is over the grid rather than in the column with
        // it: the lift is what says the pill and the pictures are on two planes.
        className="pointer-events-auto flex h-11 items-center gap-4 rounded-full bg-surface px-1 shadow-md"
      >
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                roundness="full"
                aria-label={clearLabel}
                onClick={onClear}
              >
                <XIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{clearLabel}</TooltipContent>
          </Tooltip>

          {/* The height needs the same variant the component sets it with. A plain `h-4` does not
              replace `data-[orientation=vertical]:h-full`, which then resolves against a parent of
              no fixed height and draws nothing. */}
          <Separator orientation="vertical" className="data-[orientation=vertical]:h-4" />

          {/* Indeterminate rather than unchecked while a few are ticked: unchecked would say
              nothing is selected, next to a number saying three things are. */}
          <Checkbox
            className="ml-1"
            aria-label={t('brandMedia.selectAllShown')}
            checked={allShownSelected ? true : 'indeterminate'}
            onCheckedChange={onToggleAllShown}
          />

          <span className="text-sm font-medium tabular-nums text-foreground">
            {t('brandMedia.selectedCount', { count })}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {/* The span takes the pointer events a disabled button drops, so the reason still shows. */}
          {canManageBrandAssets ? (
            <MoveToFolderMenu
              folders={folders}
              label={t('brandMedia.moveTo')}
              className="rounded-full"
              onMove={onMove}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button variant="outline" size="sm" roundness="full" disabled>
                    {t('brandMedia.moveTo')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{noPermission}</TooltipContent>
            </Tooltip>
          )}

          {/* An icon, where the move beside it keeps its words: the two are not a pair of equal
              choices, and the one that cannot be undone is the one worth making people aim at.
              Outlined rather than filled red, which is how the detail panel draws the same delete:
              a bar that is on screen for as long as a selection is reads as a warning about the
              selection if one of its controls is solid red. The confirmation is what guards it. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="outline"
                  size="icon-sm"
                  roundness="full"
                  aria-label={deleteLabel}
                  disabled={!canManageBrandAssets}
                  onClick={onDelete}
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{canManageBrandAssets ? deleteLabel : noPermission}</TooltipContent>
          </Tooltip>
        </div>
      </motion.div>
    </TooltipProvider>
  );
};
